/** @format */
import { computed, Directive, forwardRef, inject, Injector, input, Signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ValidatablePlan } from 'common-typescript';
import { OtmId } from 'common-typescript/types';
import { map } from 'rxjs';
import { AppErrorHandler } from '../error-handler/app-error-handler';
import { CurriculumPeriodEntityService } from '../service/curriculum-period-entity.service';
import { PlanRuleData } from '../service/plan-rule-data.service';
import { PlanData, PlanStateObject } from '../service/plan-state.service';
import { UniversityService } from '../service/university.service';

/**
 * The plan that a plan structure shows. The edit modal and its free edit also give the validatable plan, which the components that edit
 * the plan read. Only the edit modal gives the rule data, which only rules read.
 */
export interface PlanStructureContextValues {
    planStateObject: PlanStateObject;
    planData: PlanData;
    validatablePlan?: ValidatablePlan;
    planRuleData?: PlanRuleData;
}

/**
 * The plan-wide data of a plan structure, for the components to inject instead of passing it down every level as inputs. A host provides
 * it with the sisPlanStructureContext directive around the plan structure it renders.
 */
export abstract class PlanStructureContext {
    abstract readonly planStateObject: Signal<PlanStateObject>;
    abstract readonly planData: Signal<PlanData>;
    /** Throws when the host gives no validatable plan: only the edit modal and its free edit do */
    abstract readonly validatablePlan: Signal<ValidatablePlan>;
    /** Throws when the host gives no rule data: only the edit modal does */
    abstract readonly planRuleData: Signal<PlanRuleData>;
    /**
     * The ids of the curriculum periods that the searches of the Any rules look in, starting from the curriculum period of the plan.
     * Fetched once per context, on the first call; undefined until they arrive. Needs the validatable plan, so only the hosts that give
     * it can call it.
     */
    abstract curriculumPeriodIdsForSearch(): Signal<OtmId[] | undefined>;
}

type PlanStructureContextValueSignals = Pick<PlanStructureContext, 'planStateObject' | 'planData' | 'validatablePlan' | 'planRuleData'>;

/** The signals of a plan structure context that shows the given values */
export function planStructureContextOf(values: Signal<Partial<PlanStructureContextValues>>): PlanStructureContextValueSignals {
    return {
        planStateObject: computed(() => values().planStateObject),
        planData: computed(() => values().planData),
        validatablePlan: computed(() => values().validatablePlan ?? missing('validatablePlan')),
        planRuleData: computed(() => values().planRuleData ?? missing('planRuleData')),
    };
}

const HOSTS_THAT_GIVE: Record<'validatablePlan' | 'planRuleData', string> = {
    validatablePlan: 'the plan structure edit modal and its free edit give it',
    planRuleData: 'the plan structure edit modal gives it',
};

function missing(value: keyof typeof HOSTS_THAT_GIVE): never {
    throw new Error(`The plan structure context has no ${value}: only ${HOSTS_THAT_GIVE[value]}`);
}

/**
 * Provides the plan structure context to the elements inside it. The nearest one wins, so a host can render plan structures of different
 * plans, such as the plan and a preview of it.
 */
@Directive({
    selector: '[sisPlanStructureContext]',
    providers: [{ provide: PlanStructureContext, useExisting: forwardRef(() => PlanStructureContextDirective) }],
    standalone: false,
})
export class PlanStructureContextDirective implements PlanStructureContext {
    readonly sisPlanStructureContext = input.required<PlanStructureContextValues>();

    private readonly context = planStructureContextOf(this.sisPlanStructureContext);
    readonly planStateObject = this.context.planStateObject;
    readonly planData = this.context.planData;
    readonly validatablePlan = this.context.validatablePlan;
    readonly planRuleData = this.context.planRuleData;

    private readonly injector = inject(Injector);
    private curriculumPeriodIds: Signal<OtmId[] | undefined> | undefined;

    curriculumPeriodIdsForSearch(): Signal<OtmId[] | undefined> {
        // The services are looked up only here, so that the hosts that show no rules need none of them
        this.curriculumPeriodIds ??= toSignal(
            // The edit modal edits one plan, so its curriculum period stays the same
            this.injector
                .get(CurriculumPeriodEntityService)
                .getCurriculumPeriodsForSearch(
                    this.injector.get(UniversityService).getCurrentUniversityOrgId(),
                    untracked(() => this.validatablePlan().plan.curriculumPeriodId),
                )
                .pipe(
                    map(curriculumPeriods => curriculumPeriods.map(curriculumPeriod => curriculumPeriod.id)),
                    this.injector.get(AppErrorHandler).defaultErrorHandler(),
                ),
            { injector: this.injector },
        );
        return this.curriculumPeriodIds;
    }
}
